<template>
  <div class="mt-4 text-left">
    <h4>{{ $t("form.project.basic_data") }}</h4>
    <v-row>
      <v-col cols="12">
        <MultilingualTextField v-if="load_complete" v-model="project.title" :label="$t('form.project.title')" entity-type="project" field="title" readonly />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          v-model="project.acronym"
          :label="$t('form.project.acronym')"
          readonly
          filled
          outlined
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          :value="$t('project.types.' + project.type)"
          :label="$t('form.type')"
          readonly
          filled
          outlined
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-checkbox
          v-model="project.external"
          :label="$t('form.project.external')"
          :value="project.external"
          :readonly="true"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          :value="project.doi"
          :label="$t('form.doi')"
          readonly
          filled
          outlined
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          :value="project.code"
          :label="$t('form.projectcode')"
          readonly
          filled
          outlined
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          :value="project.start_date"
          :label="$t('form.project.start_date')"
          readonly
          filled
          outlined
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          v-model="project.end_date"
          :label="$t('form.project.end_date')"
          readonly
          filled
          outlined
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row v-if="Object.keys(project.description).length > 0">
      <v-col cols="12" class="py-0 mb-5">
        <MultilingualTextField v-if="load_complete" v-model="project.description" :label="$t('form.description')" entity-type="project" field="description" rich readonly />
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12" class="py-0">
        <v-textarea
          v-model="project.description[$i18n.locale]"
          :label="$t('form.description')"
          readonly
          filled
          outlined
        ></v-textarea>
      </v-col>
    </v-row>

    <v-row v-if="Object.keys(project.objectives).length > 0">
      <v-col cols="12" class="py-0 mb-5">
        <MultilingualTextField v-if="load_complete" v-model="project.objectives" :label="$t('form.project.objectives')" entity-type="project" field="objectives" rich readonly />
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12" class="py-0">
        <v-textarea
          v-model="project.objectives[$i18n.locale]"
          :label="$t('form.project.objectives')"
          readonly
          filled
          outlined
        ></v-textarea>
      </v-col>
    </v-row>

    <v-row v-if="Object.keys(project.results).length > 0">
      <v-col cols="12" class="py-0 mb-5">
        <MultilingualTextField v-if="load_complete" v-model="project.results" :label="$t('form.project.results')" entity-type="project" field="results" rich readonly />
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12" class="py-0">
        <v-textarea
          v-model="project.results[$i18n.locale]"
          :label="$t('form.project.results')"
          readonly
          filled
          outlined
        ></v-textarea>
      </v-col>
    </v-row>
    <h4 class="text-left">{{ $t("form.project.investigators") }}</h4>
    <v-row>
      <v-col cols="12">
        <v-text-field
          :value="
            project.main_citius_investigators.map(
              p =>
                p.preferred_name.given_name + ' ' + p.preferred_name.family_name
            )
          "
          :label="$t('form.project.main_citius_investigators')"
          readonly
          filled
          outlined
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-text-field
          v-model="project.main_external_investigators"
          :label="$t('form.project.main_external_investigators')"
          readonly
          filled
          outlined
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-text-field
          :value="
            project.citius_investigators.map(
              p =>
                p.preferred_name.given_name + ' ' + p.preferred_name.family_name
            )
          "
          :label="$t('form.project.citius_investigators')"
          readonly
          filled
          outlined
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-text-field
          v-model="project.external_investigators"
          :label="$t('form.project.external_investigators')"
          readonly
          filled
          outlined
        />
      </v-col>
    </v-row>
    <h4 class="text-left">{{ $t("form.project.additional_data") }}</h4>
    <v-row>
      <v-col cols="12">
        <v-text-field
          :value="project.funder_programs.map(p => p.internal_name)"
          :label="$t('form.project.funder_programs')"
          readonly
          filled
          outlined
        ></v-text-field>
      </v-col>
    </v-row>
    <MultilingualTextField v-if="load_complete" v-model="project.funder_text" :label="$t('form.funder_text')" entity-type="project" field="funder_text" rich readonly />
    <ProjectBudgetFields :total-budget.sync="project.total_budget" :citius-budget.sync="project.citius_budget" readonly />
  <v-row>
    <v-col cols="12">
      <v-text-field
        :value="project.additional_text"
        :label="$t('form.project.additional_text')"
        readonly
        filled
        outlined
      ></v-text-field>
    </v-col>
  </v-row>
    <v-row>
      <v-col>
        <v-text-field
          :value="project.contractor != null ? project.contractor.name : ''"
          readonly
          :label="$t('form.project.contractor')"
          filled
          outlined
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-text-field
          v-model="project.uri"
          :label="$t('form.project.uri')"
          readonly
          filled
          outlined
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-text-field
          :value="
            project.research_areas.map(function(item) {
              return item.name[$i18n.locale];
            })
          "
          :label="$t('research_areas')"
          readonly
          filled
          outlined
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="d-flex justify-center">
        <v-img
          :src="$store.state.endpoint + project.image"
          :max-width="500"
          :max-height="300"
        ></v-img>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import project_data from "../../mixins/project_data";
import MultilingualTextField from "@/components/reusables/MultilingualTextField.vue";
import ProjectBudgetFields from "@/components/projects/ProjectBudgetFields.vue";
export default {
  name: "DetailDataProject",
  components: { MultilingualTextField, ProjectBudgetFields },
  props: {
    project_id: Number
  },

  data() {
    return {
      tab_title: 0,
      tab_description: 0,
      tab_objectives: 0,
      tab_results: 0,
      tab_funder_text: 0
    };
  },
  mixins: [project_data],
  created() {
    this.getProject(
      this.$store.state.endpoint + "/user/project/" + this.project_id
    );
  }
};
</script>

<style scoped></style>
